import React, { useState, useEffect, useRef } from "react";
import "./MarketingAgentBlogInputForm.css";

const BlogInputForm = ({ onGenerate }) => {
  const [sources, setSources] = useState([{ name: "", text: "" }]);
  const [preferences, setPreferences] = useState({
    authorNotes: "",
    flowNotes: "",
    tone: "",
    audienceDescription: "",
    length: "",
  });
  const [strategy, setStrategy] = useState({
    seo: "",
    companyContext: "",
    referenceBlog: "",
  });
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  // Animate on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (containerRef.current) observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  // Handle source list updates
  const handleSourceChange = (index, field, value) => {
    const updatedSources = [...sources];
    updatedSources[index][field] = value;
    setSources(updatedSources);
  };

  // Add new source entry
  const addSource = () => {
    setSources([...sources, { name: "", text: "" }]);
  };

  // Remove a source entry
  const removeSource = (index) => {
    const updatedSources = sources.filter((_, i) => i !== index);
    setSources(updatedSources);
  };

  // Handle form submission
  const handleGenerateClick = () => {
    const formData = { sources, preferences, strategy };
    onGenerate(formData);
  };

  return (
    <div ref={containerRef} className={`blog-input-form-container ${isVisible ? "visible" : ""}`}>
      <h3 className="blog-input-form-title">Blog Input Form</h3>

      {/* Content Sources */}
      <h4 className="blog-input-section-title">Content Sources</h4>
      {sources.map((source, index) => (
        <div key={index} className="blog-input-container">
          <input
            type="text"
            placeholder="Source Name"
            value={source.name}
            onChange={(e) => handleSourceChange(index, "name", e.target.value)}
            className="blog-input"
          />
          <textarea
            placeholder="Source Text"
            value={source.text}
            onChange={(e) => handleSourceChange(index, "text", e.target.value)}
            className="blog-textarea"
          />
          <button className="blog-remove-source-button" onClick={() => removeSource(index)}>
            ✖ Remove
          </button>
        </div>
      ))}
      <button className="blog-add-source-button" onClick={addSource}>+ Add Source</button>

      {/* Blog Preferences */}
      <h4 className="blog-input-section-title">Blog Preferences</h4>
      <div className="blog-input-container">
        <textarea
          placeholder="Author Notes"
          value={preferences.authorNotes}
          onChange={(e) => setPreferences({ ...preferences, authorNotes: e.target.value })}
          className="blog-textarea"
        />
        <textarea
          placeholder="Flow Notes"
          value={preferences.flowNotes}
          onChange={(e) => setPreferences({ ...preferences, flowNotes: e.target.value })}
          className="blog-textarea"
        />
        <input
          type="text"
          placeholder="Tone (e.g., professional, casual, persuasive)"
          value={preferences.tone}
          onChange={(e) => setPreferences({ ...preferences, tone: e.target.value })}
          className="blog-input"
        />
        <input
          type="text"
          placeholder="Target Audience Description"
          value={preferences.audienceDescription}
          onChange={(e) => setPreferences({ ...preferences, audienceDescription: e.target.value })}
          className="blog-input"
        />
        <select
          value={preferences.length}
          onChange={(e) => setPreferences({ ...preferences, length: e.target.value })}
          className="blog-input"
        >
          <option value="">Select Blog Length</option>
          <option value="short">Short</option>
          <option value="medium">Medium</option>
          <option value="long">Long</option>
        </select>
      </div>

      {/* Company Strategy & Goals */}
      <h4 className="blog-input-section-title">Company Strategy & Goals</h4>
      <div className="blog-input-container">
        <textarea
          placeholder="SEO Strategy"
          value={strategy.seo}
          onChange={(e) => setStrategy({ ...strategy, seo: e.target.value })}
          className="blog-textarea"
        />
        <textarea
          placeholder="Company Context"
          value={strategy.companyContext}
          onChange={(e) => setStrategy({ ...strategy, companyContext: e.target.value })}
          className="blog-textarea"
        />
        <textarea
          placeholder="Reference Blog (to mimic)"
          value={strategy.referenceBlog}
          onChange={(e) => setStrategy({ ...strategy, referenceBlog: e.target.value })}
          className="blog-textarea"
        />
      </div>

      {/* Generate Button */}
      <button className="blog-generate-button" onClick={handleGenerateClick}>
        Generate Blog
      </button>
    </div>
  );
};

export default BlogInputForm;
